/**
 * Converts a hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result && result[1] && result[2] && result[3]
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculates the relative luminance of a color
 * Based on WCAG guidelines
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs! + 0.7152 * gs! + 0.0722 * bs!;
}

/**
 * Calculates the contrast ratio between two colors
 */
function getContrastRatio(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number {
  const lum1 = getRelativeLuminance(color1.r, color1.g, color1.b);
  const lum2 = getRelativeLuminance(color2.r, color2.g, color2.b);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Converts RGB values back to a hex color string
 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Returns the color with the highest contrast ratio
 */
export function getHigherContrastColor(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);

  if (!rgb) {
    return 'var(--theme-light-foreground)';
  }

  const white = { r: 255, g: 255, b: 255 };
  const black = { r: 0, g: 0, b: 0 };

  const contrastWithWhite = getContrastRatio(rgb, white);
  const contrastWithBlack = getContrastRatio(rgb, black);

  return contrastWithWhite > contrastWithBlack
    ? 'var(--theme-dark-foreground)'
    : 'var(--theme-light-foreground)';
}

/**
 * Mixes a hex color with white using the provided primary ratio (0..1)
 */
export function mixWithWhite(hex: string, primaryRatio: number): string {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;

  const clampedRatio = Math.max(0, Math.min(1, primaryRatio));
  const white = { r: 255, g: 255, b: 255 };

  const mixed = {
    r: rgb.r * clampedRatio + white.r * (1 - clampedRatio),
    g: rgb.g * clampedRatio + white.g * (1 - clampedRatio),
    b: rgb.b * clampedRatio + white.b * (1 - clampedRatio),
  };

  return rgbToHex(mixed.r, mixed.g, mixed.b);
}
